<template>
  <div class="hiring-process-container">
    <button
      v-if="hasPrev"
      :key="module.id + '-prev'"
      class="arrow -prev"
      aria-label="Previous"
      @click="prev"
    >
      <div class="wrap">
        <Icon icon="fa6-solid:angle-left" title="Previous" />
      </div>
    </button>

    <div
      ref="container"
      class="horizontal-container"
      :style="!hasPrev && !hasNext ? 'justify-content: center;' : ''"
      tabindex="0"
      @scroll.passive="onScroll"
    >
      <Step
        v-for="step in module.modules"
        :id="step.id"
        :key="step.id"
        :module="step"
        :parent-module="module"
      />
    </div>

    <button
      v-if="hasNext"
      :key="module.id + '-next'"
      class="arrow -next"
      aria-label="Next"
      @click="next"
    >
      <div class="wrap">
        <Icon icon="fa6-solid:angle-right" title="Next" />
      </div>
    </button>
  </div>
</template>

<script>
import hiringProcessModule from "../hiringProcessDefaultModuleProp.js";
import {
  globalComputed,
  globalMethods,
} from "../../../GlobalUI/mixins/global.js";
import Step from "../../Step/Step.vue";

import { Icon } from "@iconify/vue";

const delta = 2.5;

// basically a port of https://github.com/fuxingloh/vue-horizontal
// that works with both Vue 2 and 3
export default {
  name: "HiringProcess",
  components: {
    Step,
    Icon,
  },
  mixins: [globalComputed, globalMethods],
  props: {
    module: {
      type: Object,
      default() {
        return hiringProcessModule;
      },
    },
  },
  data() {
    return {
      hasPrev: false,
      hasNext: false,
    };
  },
  computed: {
    slots() {
      // eslint-disable-next-line vue/require-slots-as-functions
      return this.$slots?.default?.filter((s) => s.tag) || [];
    },
  },
  mounted() {
    this.onScrollDebounce();
    window.addEventListener("resize", this.onScrollDebounce);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onScrollDebounce);
  },
  methods: {
    findPrevSlot(x) {
      const slots = this.slots;

      for (let i = 0; i < slots.length; i++) {
        const rect = slots[i].elm.getBoundingClientRect();

        if (rect.left <= x && x <= rect.right) {
          return slots[i];
        }

        if (x <= rect.left) {
          return slots[i];
        }
      }
    },
    findNextSlot(x) {
      const slots = this.slots;

      for (let i = 0; i < slots.length; i++) {
        const rect = slots[i].elm.getBoundingClientRect();

        if (rect.right <= x) {
          continue;
        } else if (rect.left <= x) {
          return slots[i];
        }

        if (x <= rect.left) {
          return slots[i];
        }
      }
    },
    prev() {
      const container = this.$refs.container;
      const left = container.getBoundingClientRect().left;
      const x = left + container.clientWidth * -1 - delta;
      const slot = this.findPrevSlot(x);

      if (slot) {
        const width = slot.elm.getBoundingClientRect().left - left;
        this.scrollToLeft(container.scrollLeft + width);
        return;
      }

      const width = container.clientWidth;
      this.scrollToLeft(container.scrollLeft - width);
    },
    next() {
      const container = this.$refs.container;
      const left = container.getBoundingClientRect().left;
      const x = left + container.clientWidth + delta;
      const slot = this.findNextSlot(x);

      if (slot) {
        const width = slot.elm.getBoundingClientRect().left - left;
        if (width > delta) {
          this.scrollToLeft(container.scrollLeft + width);
          return;
        }
      }

      const width = container.clientWidth;
      this.scrollToLeft(container.scrollLeft + width);
    },
    scrollToLeft(left, behavior = "smooth") {
      const element = this.$refs.container;
      element.scrollTo({ left: left, behavior: behavior });
    },
    onScroll() {
      const container = this.$refs.container;
      this.$emit("scroll", {
        left: container.scrollLeft,
      });

      clearTimeout(this.debounceId);
      // @ts-ignore
      this.debounceId = setTimeout(this.onScrollDebounce, 100);
    },
    onScrollDebounce() {
      this.refresh((data) => {
        this.$emit("scroll-debounce", data);
      });
    },
    refresh(callback) {
      this.$nextTick(() => {
        const data = this.calculate();

        this.left = data.left;
        this.width = data.width;
        this.scrollWidth = data.scrollWidth;
        this.hasNext = data.hasNext;
        this.hasPrev = data.hasPrev;

        callback(data);
      });
    },
    calculate() {
      const container = this.$refs.container;
      const slot0 = this.slots[0]?.elm;

      function hasNext() {
        return (
          container.scrollWidth >
          container.scrollLeft + container.clientWidth + delta
        );
      }

      function hasPrev() {
        if (container.scrollLeft === 0) {
          return false;
        }

        const containerVWLeft = container.getBoundingClientRect().left;
        const slot0VWLeft = slot0?.getBoundingClientRect()?.left ?? 0;
        return Math.abs(containerVWLeft - slot0VWLeft) >= delta;
      }

      return {
        left: container.scrollLeft,
        width: container.clientWidth,
        scrollWidth: container.scrollWidth,
        hasNext: hasNext(),
        hasPrev: hasPrev(),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@include reset;

.hiring-process-container {
  position: relative;
  margin: auto;

  width: 100%;

  padding-inline: var(--cr-module-padding-large);

  @include laptop {
    padding-inline: calc(var(--cr-module-padding-large) / 2);
  }

  @include mobile {
    padding-inline: 25px;
  }
}

.horizontal-container {
  width: 100%;
  margin: 0;
  padding: 0;

  position: relative;

  display: flex;
  gap: 16px;

  border: none;
  box-sizing: content-box;
  overflow-x: scroll;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  -webkit-scrollbar-width: none;
  -ms-overflow-style: none;
}

.arrow {
  @include reset-button;
  --arrow-size: 50px;

  position: absolute;
  align-self: center;
  z-index: 1;
  top: 0;
  bottom: 0;
  height: 100%;

  &.-prev {
    left: calc(var(--arrow-size) * 2);

    @include laptop {
      left: var(--arrow-size);
    }

    @include tablet {
      left: 0;
    }
  }

  &.-next {
    right: calc(var(--arrow-size) * 2);

    @include laptop {
      right: var(--arrow-size);
    }

    @include tablet {
      right: 0;
    }
  }

  .wrap {
    display: flex;
    align-items: center;
    justify-content: center;

    width: var(--arrow-size);
    height: var(--arrow-size);
    border-radius: var(--arrow-size);

    background-color: #ffffffbf;
  }
}
</style>
